// SCR-STAFF-500 — میز کار ارائه‌دهنده (FLOW-07؛ US-CLN-080 تا 082): فهرست امروز با وضعیت مواجهه و یادداشت، «فراخوان بعدی»، شمارنده کارهای معوق
// و فضای کار مواجهه. کلیک روی ردیف = شروع مواجهه با یک کلیک (US-CLN-010)؛ منع مطلق ← دیالوگ Override (فقط پزشک — AC-CLN-061.3)؛ پرسش
// اجباری بارداری (AC-CLN-006.2).
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type ClnFindingView, type ClnWorkbenchDayItem } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { EmptyState, ErrorState, LoadingState, PageHeader, toJalali, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { clnKeys, errorCode, OverrideDialog, PENDING_PATH, SCR_STAFF_500 } from './common';
import { CredentialAlertsBanner } from '../iam/CredentialAlertsBanner';
import { InsurerCredentialStatusCards } from '../integrations/InsurerCredentialStatusCards';
import { EncounterWorkspace } from './EncounterWorkspace';

/** پرسش اجباری «باردار هستید؟» پیش از شروع خدمت وابسته به بارداری (AC-CLN-006.2). */
function PregnancyDialog({ open, busy, onAnswer, onCancel }: { open: boolean; busy: boolean; onAnswer: (pregnant: boolean) => void; onCancel: () => void }) {
  const { t } = useTranslation('cln');
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} aria-labelledby="cln-pregnancy-title" maxWidth="xs" fullWidth data-testid="cln-pregnancy">
      <DialogTitle id="cln-pregnancy-title">{t('cln:pregnancy.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('cln:pregnancy.question')}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t('cln:pregnancy.cancel')}
        </Button>
        <Button onClick={() => onAnswer(true)} disabled={busy} color="error" data-testid="cln-pregnancy-yes">
          {t('cln:pregnancy.yes')}
        </Button>
        <Button onClick={() => onAnswer(false)} disabled={busy} variant="contained" data-testid="cln-pregnancy-no">
          {t('cln:pregnancy.no')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface StartState {
  patientId: string;
  appointmentId: string | null;
  serviceIds: string[];
}

/** شروع مواجهه با مدیریت منع مطلق و پرسش بارداری (مشترک میز کار و پرونده). */
export function useStartEncounter(onStarted: (encounterId: string) => void) {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [blocked, setBlocked] = useState<{ start: StartState; findings: ClnFindingView[]; canOverride: boolean } | null>(null);
  const [pregnancy, setPregnancy] = useState<StartState | null>(null);
  const start = useMutation({
    mutationFn: (state: StartState) =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/encounters', {
          body: { patientId: state.patientId, appointmentId: state.appointmentId, type: null, serviceIds: state.appointmentId ? null : state.serviceIds, overrideIds: null },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (view) => {
      setBlocked(null);
      void queryClient.invalidateQueries({ queryKey: clnKeys.all(session?.tenantId) });
      onStarted(view.id);
    },
    onError: async (error, state) => {
      const code = errorCode(error);
      if (code === 'PHX-CLN-0211') {
        setPregnancy(state);
      } else if (code === 'PHX-CLN-0210' && state.serviceIds.length > 0) {
        const check = await unwrap(
          tenantApi.POST('/api/v1/clinical/patients/{patientId}/contraindications:check', {
            params: { path: { patientId: state.patientId } },
            body: { serviceIds: state.serviceIds, at: null },
          }),
        );
        setBlocked({ start: state, findings: check.items.flatMap((item) => item.findings), canOverride: check.canOverride && access.can('cln.alert.override') });
      }
    },
  });
  const answer = useMutation({
    mutationFn: (pregnant: boolean) =>
      unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/pregnancy-checks', { params: { path: { patientId: pregnancy?.patientId ?? '' } }, body: { pregnant, estimatedDueDate: null } })),
    onSuccess: () => {
      const state = pregnancy;
      setPregnancy(null);
      if (state) {
        start.mutate(state);
      }
    },
  });
  const dialogs = (
    <>
      {blocked && (
        <OverrideDialog
          open
          patientId={blocked.start.patientId}
          findings={blocked.findings}
          canOverride={blocked.canOverride}
          appointmentId={blocked.start.appointmentId}
          onClose={() => setBlocked(null)}
          onOverridden={() => start.mutate(blocked.start)}
        />
      )}
      <PregnancyDialog open={pregnancy !== null} busy={answer.isPending} onAnswer={(pregnant) => answer.mutate(pregnant)} onCancel={() => setPregnancy(null)} />
    </>
  );
  return { start, dialogs };
}

export function WorkbenchPage() {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const [params, setParams] = useSearchParams();
  const selected = params.get('encounter');
  const select = (id: string | null) => setParams(id ? { encounter: id } : {}, { replace: true });
  const day = useQuery({
    queryKey: clnKeys.day(session?.tenantId, 'today'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/my/day')),
    refetchInterval: 60_000,
  });
  const { start, dialogs } = useStartEncounter(select);
  const canStart = access.accessMode === 'Full' && access.can('cln.encounter.manage');
  const open = (item: ClnWorkbenchDayItem) => {
    if (item.encounterId) {
      select(item.encounterId);
    } else if (canStart && (item.status === 'CheckedIn' || item.status === 'InProgress')) {
      start.mutate({ patientId: item.patientId, appointmentId: item.appointmentId, serviceIds: item.serviceIds });
    }
  };
  const next = day.data?.items.find((item) => item.appointmentId === day.data?.nextAppointmentId);
  const time = (value: string) => toJalali(value, { timeZone, pattern: 'HH:mm' });

  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_500}>
      <PageHeader titleKey="cln:workbench.title" titleValues={{ name: day.data?.providerName ?? '' }} subtitleKey="cln:workbench.subtitle" />
      <CredentialAlertsBanner providerId={day.data?.providerId ?? null} />
      <InsurerCredentialStatusCards />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4, lg: 3 }}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-day">
              <Typography variant="subtitle1" component="h2" sx={{ mb: 1 }}>
                {t('cln:workbench.today')}
              </Typography>
              {day.isPending ? (
                <LoadingState rows={3} />
              ) : day.error ? (
                <ErrorState error={day.error} compact onRetry={() => void day.refetch()} />
              ) : day.data.providerId === null ? (
                <Alert severity="info" data-testid="cln-day-no-provider">
                  {t('cln:workbench.no_provider')}
                </Alert>
              ) : day.data.items.length === 0 ? (
                <EmptyState titleKey="cln:workbench.empty" />
              ) : (
                <List dense component="div" aria-label={t('cln:workbench.today')}>
                  {day.data.items.map((item) => (
                    <ListItemButton
                      key={item.appointmentId}
                      selected={Boolean(item.encounterId) && item.encounterId === selected}
                      onClick={() => open(item)}
                      disabled={start.isPending}
                      data-testid="cln-day-row"
                      data-appointment={item.appointmentId}
                      data-status={item.status}
                    >
                      <ListItemText
                        primary={`${time(item.start)} · ${item.patientName ?? '—'}`}
                        secondary={[
                          t(`cln:appointment_status.${item.status}`),
                          item.encounterStatus ? t(`cln:encounter_status.${item.encounterStatus}`) : null,
                          item.noteStatus ? t(`cln:note_status.${item.noteStatus}`) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
              {next && canStart && (
                <Button fullWidth variant="contained" sx={{ mt: 1 }} onClick={() => open(next)} disabled={start.isPending} data-testid="cln-call-next">
                  {t('cln:workbench.call_next', { name: next.patientName ?? '' })}
                </Button>
              )}
              {start.error && !['PHX-CLN-0210', 'PHX-CLN-0211'].includes(errorCode(start.error) ?? '') ? <ErrorState error={start.error} compact /> : null}
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-pending-card">
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <Badge color="error" badgeContent={day.data?.pendingCount ?? 0} showZero data-testid="cln-pending-count">
                  <Typography variant="subtitle1" component="h2">
                    {t('cln:pending.title')}
                  </Typography>
                </Badge>
              </Stack>
              <Link component={RouterLink} to={PENDING_PATH} sx={{ display: 'inline-block', mt: 1 }}>
                {t('cln:pending.open')}
              </Link>
            </Paper>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9 }}>
          {selected ? (
            <EncounterWorkspace key={selected} encounterId={selected} />
          ) : (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <EmptyState titleKey="cln:workbench.select" descriptionKey="cln:workbench.select_hint" />
            </Paper>
          )}
        </Grid>
      </Grid>
      {dialogs}
    </Stack>
  );
}
